import React, { useEffect, useRef, useState } from 'react';
import { Settings as SettingsIcon, Save, Upload, Trash2, Image as ImageIcon } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { invalidateBrandingCache } from '../components/BrandLogo';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const [formData, setFormData] = useState({
    company_name: '',
    admin_notification_email: '',
    max_upload_size: 1073741824,
    submissions_enabled: true
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const { data } = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(data);
      setLogoUrl(data.logo_url || null);
      setFormData({
        company_name: data.company_name || 'RetouchingPix',
        admin_notification_email: data.admin_notification_email || '',
        max_upload_size: data.max_upload_size || 1073741824,
        submissions_enabled: data.submissions_enabled !== false
      });
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/admin/settings`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Settings saved successfully');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PNG, JPG, WEBP, GIF or SVG image');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo file is too large. Maximum size is 2MB.');
      return;
    }

    setUploadingLogo(true);
    try {
      const token = localStorage.getItem('admin_token');
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/admin/upload-logo`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setLogoUrl(data.logo_url);
      invalidateBrandingCache();
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    setUploadingLogo(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/logo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogoUrl(null);
      invalidateBrandingCache();
      toast.success('Logo removed');
    } catch (error) {
      toast.error('Failed to remove logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      toast.error('Please fill all password fields');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setChangingPassword(true);

    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/admin/change-password`,
        {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        },
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: {
            current_password: passwordData.current_password,
            new_password: passwordData.new_password
          }
        }
      );
      toast.success('Password changed successfully');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-zinc-900"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-settings" className="space-y-8">
        <div>
          <h1 className="text-3xl font-semibold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Settings
          </h1>
          <p className="text-zinc-600 mt-1">Manage your application settings</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Application Settings */}
          <form onSubmit={handleSave}>
            <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm space-y-6">
              <h2 className="text-xl font-semibold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Application Settings
              </h2>

              {/* Company Logo */}
              <div>
                <Label data-testid="logo-label" className="text-sm font-medium mb-2 block">
                  Company Logo
                </Label>
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 rounded-lg border border-zinc-200 bg-zinc-50 flex items-center justify-center overflow-hidden shrink-0">
                    {logoUrl ? (
                      <img
                        data-testid="logo-preview"
                        src={logoUrl}
                        alt="Company logo"
                        className="max-h-16 max-w-16 object-contain"
                      />
                    ) : (
                      <ImageIcon className="w-6 h-6 text-zinc-400" />
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <input
                      ref={logoInputRef}
                      data-testid="logo-file-input"
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                      onChange={handleLogoSelect}
                      className="hidden"
                    />
                    <div className="flex items-center gap-2">
                      <Button
                        data-testid="upload-logo-btn"
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={uploadingLogo}
                        onClick={() => logoInputRef.current?.click()}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {uploadingLogo ? 'Uploading...' : (logoUrl ? 'Replace Logo' : 'Upload Logo')}
                      </Button>
                      {logoUrl && (
                        <Button
                          data-testid="remove-logo-btn"
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={uploadingLogo}
                          onClick={handleRemoveLogo}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-zinc-500">
                      PNG, JPG, WEBP, GIF or SVG. Max 2MB.
                    </p>
                  </div>
                </div>
              </div>

              {/* Company Name */}
              <div>
                <Label data-testid="company-name-label" className="text-sm font-medium mb-2 block">
                  Company Name
                </Label>
                <Input
                  data-testid="company-name-input"
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="RetouchingPix"
                  className="h-11"
                />
              </div>

              {/* Admin Notification Email */}
              <div>
                <Label data-testid="notification-email-label" className="text-sm font-medium mb-2 block">
                  Admin Notification Email
                </Label>
                <Input
                  data-testid="notification-email-input"
                  type="email"
                  value={formData.admin_notification_email}
                  onChange={(e) => setFormData({ ...formData, admin_notification_email: e.target.value })}
                  placeholder="admin@retouchingpix.com"
                  className="h-11"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  This email will receive notifications for new test job submissions
                </p>
              </div>

              {/* Max Upload Size */}
              <div>
                <Label data-testid="max-upload-label" className="text-sm font-medium mb-2 block">
                  Maximum Upload Size (GB)
                </Label>
                <Input
                  data-testid="max-upload-input"
                  type="number"
                  value={formData.max_upload_size / 1073741824}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    max_upload_size: parseFloat(e.target.value) * 1073741824 
                  })}
                  step="0.1"
                  min="0.1"
                  max="10"
                  className="h-11"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Maximum total file size per submission (default: 1GB)
                </p>
              </div>

              {/* Enable/Disable Submissions */}
              <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-lg">
                <div>
                  <Label data-testid="submissions-enabled-label" className="text-sm font-medium">
                    Enable Test Job Submissions
                  </Label>
                  <p className="text-xs text-zinc-500 mt-1">
                    Toggle to temporarily disable new submissions
                  </p>
                </div>
                <Switch
                  data-testid="submissions-enabled-switch"
                  checked={formData.submissions_enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, submissions_enabled: checked })}
                />
              </div>

              {/* Save Button */}
              <div className="pt-4 border-t border-zinc-200">
                <Button
                  data-testid="save-settings-btn"
                  type="submit"
                  disabled={saving}
                  className="w-full h-11 bg-zinc-900 text-white hover:bg-zinc-800"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </div>
          </form>

          {/* Change Password */}
          <form onSubmit={handleChangePassword}>
            <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm space-y-6">
              <h2 className="text-xl font-semibold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Change Password
              </h2>

              {/* Current Password */}
              <div>
                <Label data-testid="current-password-label" className="text-sm font-medium mb-2 block">
                  Current Password
                </Label>
                <Input
                  data-testid="current-password-input"
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  placeholder="Enter current password"
                  className="h-11"
                />
              </div>

              {/* New Password */}
              <div>
                <Label data-testid="new-password-label" className="text-sm font-medium mb-2 block">
                  New Password
                </Label>
                <Input
                  data-testid="new-password-input"
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  placeholder="Enter new password"
                  className="h-11"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Password must be at least 6 characters
                </p>
              </div>

              {/* Confirm New Password */}
              <div>
                <Label data-testid="confirm-password-label" className="text-sm font-medium mb-2 block">
                  Confirm New Password
                </Label>
                <Input
                  data-testid="confirm-password-input"
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  placeholder="Confirm new password"
                  className="h-11"
                />
              </div>

              {/* Change Password Button */}
              <div className="pt-4 border-t border-zinc-200">
                <Button
                  data-testid="change-password-btn"
                  type="submit"
                  disabled={changingPassword}
                  variant="outline"
                  className="w-full h-11"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;